import { Button, Dialog, DialogActions, DialogContent, Grid2, Stack, Step, StepLabel, Stepper, Tooltip } from '@mui/material'
import { makeStyles } from '@mui/styles'
import Icon from 'components/icon/Icon'
import { DefaultDialogTitle } from 'components/styled/Dialog'
import { StyledFieldSet } from 'components/StyledElements'
import { push } from '@lagunovsky/redux-react-router'
import CmsAction from 'events/actions/CmsAction'
import PropTypes from 'prop-types'
import React, { useEffect, useState } from 'react'
import { shallowEqual, useDispatch, useSelector } from 'react-redux'
import { useParams } from 'react-router-dom'
import i18n from 'simple-react-i18n'
import useTitle from 'utils/customHook/useTitle'
import { getExportAction } from 'utils/VisitUtils'
import { H_CAMPAIGN_INSTALLATION_VALIDATION } from '../../../account/constants/AccessRulesConstants'
import CampaignAction from '../../../campaign/actions/CampaignAction'
import DtoCampaignVisit from '../../../campaign/dto/DtoCampaignVisit'
import ProgressCard from '../../../components/card/ProgressCard'
import { NewTable } from 'components/datatable/NewTable'
import ReferencialAction from '../../../referencial/action/ReferencialAction'
import { nbPerPageLabel, SANDRE } from '../../../referencial/constants/ReferencialConstants'
import { STEP } from '../../../station/constants/CampaignConstants'
import { isInstallationType } from '../../../utils/CampaignUtil'
import { getDate } from '../../../utils/DateUtil'
import { downloadURI, exportFile } from '../../../utils/ExportDataUtil'
import { componentHasHabilitations } from '../../../utils/HabilitationUtil'
import { hasValue } from '../../../utils/NumberUtil'
import { arrayOf, getLabel } from '../../../utils/StoreUtils'
import { getSandreLabel } from '../../../utils/StringUtil'
import InstallationAction from '../../actions/InstallationAction'
import InstVisitInfos from './validation/InstVisitInfos'
import InstVisitModifications from './validation/InstVisitModifications'
import InstVisitActions from './validation/InstVisitActions'
import InstVisitConclusion from './validation/InstVisitConclusion'
import InstVisitDanger from './validation/InstVisitDanger'
import InstVisitDiagnostic from './validation/InstVisitDiagnostic'
import FileDownloadIcon from '@mui/icons-material/FileDownload'
import CloseIcon from '@mui/icons-material/Close'
import EditIcon from '@mui/icons-material/Edit'

const headers = ['nullValue2', 'installationType', 'code', 'city', 'name', 'contact', 'mainTechnician', 'visitDate', 'nbActions', 'status', 'nullValue3', 'nullValue']

const useStyles = makeStyles(() => ({
    root: {
        fontSize: '15px',
        fontWeight: 'bold',
    },
    label: {
        '&.MuiStepLabel-alternativeLabel': {
            marginTop: '3px !important',
        },
    },
}))

const FINALISED_VISIT = 2

const InstallationCampaignValidationApp = () => {
    const dispatch = useDispatch()
    const { id } = useParams()
    const classes = useStyles()

    const [dataLoaded, setDataLoaded] = useState(false)
    const [progress, setProgress] = useState(0)
    const [open, setOpen] = useState(false)
    const [step, setStep] = useState(STEP.INFORMATIONS)
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
    const [installationToDelete, setInstallationToDelete] = useState(null)

    const {
        campaign,
        cities,
        contacts,
        contributors,
        sandreCodes,
        visit,
        installation,
        installationsTypes,
        campaignVisits,
    } = useSelector(store => ({
        campaign: store.CampaignReducer.campaign,
        cities: store.CityReducer.cities,
        contacts: store.ContactReducer.contacts,
        contributors: store.ContributorReducer.contributors,
        sandreCodes: store.ReferencialReducer.sandreCodes,
        visit: store.InstallationReducer.visit,
        installation: store.InstallationReducer.installation,
        installationsTypes: store.InstallationReducer.installationsTypes,
        campaignVisits: store.CampaignReducer.campaignVisits,
    }), shallowEqual)

    useEffect(() => {
        if (!componentHasHabilitations(H_CAMPAIGN_INSTALLATION_VALIDATION)) { // A modifier quand react-router sera à jour
            dispatch(push('/unauthorized'))
            return
        }

        if (!contacts.length || !contributors.length) {
            dispatch(CampaignAction.loadCampaignDashboard(p => setProgress(p))).then(() => setDataLoaded(true))
        } else {
            setDataLoaded(true)
        }

        if (!sandreCodes.length) {
            dispatch(ReferencialAction.fetchSandreCodes())
        }

        if (!installationsTypes.length) {
            dispatch(InstallationAction.fetchInstallationTypes())
        }

        dispatch(CampaignAction.fetchCampaignVisits(id))
        $('.material-tooltip').remove()
    }, [])

    useTitle(() => {
        return [
            {
                title: i18n.installation,
                href: '/installation',
            },
            {
                title: i18n.campaigns,
                href: '/installation/campaign',
            },
            {
                title: campaign.name || id,
                href: `/installation/campaign/${id}/dashboard`,
            },
            {
                title: i18n.validation,
                href: `/installation/campaign/${id}/validation`,
            },
        ]
    }, [campaign])

    const onNextStep = () => {
        if (step === STEP.INFORMATIONS && !hasValue(visit.idSurvey)) {
            setStep(STEP.MODIFICATIONS)
        } else {
            setStep(step + 1)
        }
    }

    const onPrevStep = () => {
        if (step === STEP.MODIFICATIONS && !hasValue(visit.idSurvey)) {
            setStep(STEP.INFORMATIONS)
        } else {
            setStep(step - 1)
        }
    }

    const getDialogContent = () => {
        const equipmentType = isInstallationType(installation.installationType)
        const code = installation.code ? `[${installation.code}]` : ''
        const name = installation.name ? `${installation.name}` : ''
        const type = installationsTypes.find(i => i.id === installation.installationType)?.name
        const campaignType = campaign?.campaignType
            ? getSandreLabel(sandreCodes, SANDRE.CAMPAGNES_TYPE, campaign?.campaignType)
            : i18n.notDefined

        const actions = [{
            icon: 'edit',
            tooltip: i18n.modifVisit,
            onClick: () => dispatch(push(`/installation/campaign/${visit.idCampaign}/visit/${visit.idInstallation}/${step}`)),
        }, step === STEP.ACTIONS && {
            icon: 'file_download',
            tooltip: i18n.export,
            onClick: () => {
                setOpen(false)
                exportFile({
                    data: [
                        {
                            ...getExportAction(sandreCodes, equipmentType, visit.link_actions[0]),
                            headers: ['risk', 'localisation', 'equipmentType', 'responsible', 'category', 'deadline', 'interventionType', 'comment', 'correctionDate', 'commentCorrection'],
                        },
                        ...visit.link_actions.slice(1).map(d => getExportAction(sandreCodes, equipmentType, d)),
                    ],
                    titleFile: `${i18n.actions}_${installation.name || installation.code}`,
                })
            },
        }].filter(d => !!d)

        return (
            <>
                <StyledFieldSet style={{ position: 'sticky', top: '10', zIndex: '1' }}>
                    <Grid2 container sx={{ paddingLeft: 1, fontSize: '14.3px' }}>
                        <Grid2 size={6}>
                            <>
                                {`${code} ${name} - ${type} `}
                            </>
                            <>
                                {`${i18n.campaignType}: ${campaignType || i18n.notDefined}`}
                            </>
                        </Grid2>
                        <Grid2 container size={6} justifyContent='flex-end' alignItems='center'>
                            {actions.map(action => <Icon key={action} {...action} />)}
                        </Grid2>
                    </Grid2>
                </StyledFieldSet>
                {step === STEP.INFORMATIONS && (
                    <StyledFieldSet>
                        <InstVisitInfos visit={visit} cmsSurveyId={visit.idSurvey} />
                    </StyledFieldSet>
                )}
                {step === STEP.DIAGNOSTIC && (
                    <StyledFieldSet>
                        <InstVisitDiagnostic visit={visit} />
                    </StyledFieldSet>
                )}
                {step === STEP.DANGERS && (
                    <StyledFieldSet>
                        <InstVisitDanger visit={visit} />
                    </StyledFieldSet>
                )}
                {step === STEP.MODIFICATIONS && (
                    <StyledFieldSet>
                        <InstVisitModifications visit={visit} />
                    </StyledFieldSet>
                )}
                {step === STEP.ACTIONS && <InstVisitActions visit={visit} />}
                {step === STEP.CONCLUSION && (
                    <StyledFieldSet>
                        <InstVisitConclusion
                            visit={visit}
                            launchSave={() => {
                                dispatch(InstallationAction.updateVisit(visit.idVisit, { ...visit, statusVisit: 2 })).then(() => dispatch(CampaignAction.fetchCampaignVisits(visit.idCampaign)))
                            }}
                        />
                    </StyledFieldSet>
                )}
            </>
        )
    }

    const getActiveStep = () => {
        if (step === STEP.INFORMATIONS) {
            return 0
        }
        return hasValue(visit.idSurvey) ? step - 1 : step - 3
    }

    const createAndDownloadEdition = (campaignCode, idInstallation) => {
        dispatch(InstallationAction.getEditionInstallation(campaignCode, idInstallation)).then(json => {
            downloadURI(json.targetPath)
        })
    }

    useEffect(() => {
        if (visit.idSurvey && installation.id) {
            dispatch(CmsAction.fetchCMSSurveyRestricted(visit.idSurvey, installation.id))
        }
    }, [visit.idSurvey, installation.id])

    const getDataWithOnclick = (displayValue, campaignVisit) => {
        return {
            value: displayValue,
            onClick: () => {
                if (campaignVisit.visitStatus === FINALISED_VISIT) {
                    dispatch(push(`/installation/campaign/${id}/visit/summary/${campaignVisit.idInstallation}`))
                } else {
                    dispatch(InstallationAction.fetchInstallation(campaignVisit.idInstallation)).then(() => {
                        dispatch(InstallationAction.fetchVisit(campaignVisit.idInstallation, id)).then(() => {
                            setOpen(true)
                        })
                    })
                }
            },
        }
    }

    if (dataLoaded) {
        const stations = campaignVisits.map(cv => {
            const contact = contacts.find(c => c.id === cv.ownerCode) || {}
            return {
                cv,
                installationType: getDataWithOnclick((installationsTypes.find(i => i.id === cv.installationType) || {}).name, cv),
                code: getDataWithOnclick(cv.code, cv),
                city: getDataWithOnclick(cv.townCode ? `${getLabel(cities, cv.townCode)} [${cv.townCode}]` : null, cv),
                name: getDataWithOnclick(cv.name, cv),
                contact: getDataWithOnclick(contact.name, cv),
                mainTechnician: getDataWithOnclick(cv.technicianLogin, cv),
                visitDate: getDataWithOnclick(getDate(cv.visitDate), cv),
                status: getDataWithOnclick(
                    getSandreLabel(
                        sandreCodes,
                        SANDRE.VISITES_STATUT,
                        hasValue(cv.visitStatus) ? cv.visitStatus : -1,
                    ), cv,
                ),
                nbActions: getDataWithOnclick(cv.nbActions, cv),
                nullValue: { value: (
                    <Tooltip title={i18n.export}>
                        <FileDownloadIcon
                            onClick={() => createAndDownloadEdition(campaign.id, cv.idInstallation)}
                            sx={{ cursor: 'pointer', fontSize: '2rem' }}
                        />
                    </Tooltip>
                ) },
                nullValue2: { value: cv.visitStatus !== FINALISED_VISIT ? (
                    <Tooltip title={i18n.delete}>
                        <CloseIcon
                            onClick={() => {
                                setInstallationToDelete(cv.idInstallation)
                                setDeleteDialogOpen(true)
                            }}
                            sx={{ cursor: 'pointer', fontSize: '2rem' }}
                        />
                    </Tooltip>
                ) : null },
                nullValue3: cv.nbActions > 0 ? {
                    value: (
                        <Tooltip title={i18n.operatorFeedback}>
                            <EditIcon
                                onClick={() => dispatch(push(`/installation/campaign/${campaign.id}/visit/${cv.idInstallation}/operatorFeedback`))}
                                sx={{ cursor: 'pointer', fontSize: '2rem' }}
                            />
                        </Tooltip>
                    ),
                } : null,
            }
        })

        return (
            <Grid2 sx={{ padding: '0.5rem 0.5rem 0.5rem 1rem' }}>
                <NewTable
                    rows={stations}
                    headers={headers}
                    rowsPerPageOptions={nbPerPageLabel}
                />
                <Dialog
                    open={open}
                    maxWidth='lg'
                    onClose={() => setOpen(false)}
                    slotProps={{
                        paper: {
                            sx: {
                                minHeight: '90vh',
                                maxHeight: '90vh',
                            },
                        },
                    }}
                >
                    <DefaultDialogTitle
                        title={i18n.title}
                        onClose={() => setOpen(false)}
                    />
                    <DialogContent>
                        <Grid2 container justifyContent='center' style={{ backgroundColor: 'white', paddingTop: '5px' }}>
                            <Grid2 size={12}>
                                <Stepper
                                    alternativeLabel
                                    style={{ zoom: '2' }}
                                    activeStep={getActiveStep()}
                                >
                                    <Step
                                        sx={{ cursor: 'pointer' }}
                                        onClick={() => setStep(STEP.INFORMATIONS)}
                                    >
                                        <StepLabel classes={classes}>{i18n.visit}</StepLabel>
                                    </Step>
                                    {hasValue(visit.idSurvey) && (
                                        <Step
                                            sx={{ cursor: 'pointer' }}
                                            onClick={() => setStep(STEP.DIAGNOSTIC)}
                                        >
                                            <StepLabel classes={classes}>{i18n.diagnostic}</StepLabel>
                                        </Step>
                                    )}
                                    {hasValue(visit.idSurvey) && (
                                        <Step
                                            sx={{ cursor: 'pointer' }}
                                            onClick={() => setStep(STEP.DANGERS)}
                                        >
                                            <StepLabel classes={classes}><div style={{ whiteSpace: 'pre-line' }}>{i18n.dangerousSituations}</div></StepLabel>
                                        </Step>
                                    )}
                                    <Step
                                        sx={{ cursor: 'pointer' }}
                                        onClick={() => setStep(STEP.MODIFICATIONS)}
                                    >
                                        <StepLabel classes={classes}>{i18n.modifications}</StepLabel>
                                    </Step>
                                    <Step
                                        sx={{ cursor: 'pointer' }}
                                        onClick={() => setStep(STEP.ACTIONS)}
                                    >
                                        <StepLabel classes={classes}>{i18n.actions}</StepLabel>
                                    </Step>
                                    <Step
                                        sx={{ cursor: 'pointer' }}
                                        onClick={() => setStep(STEP.CONCLUSION)}
                                    >
                                        <StepLabel classes={classes}>{i18n.conclusion}</StepLabel>
                                    </Step>
                                </Stepper>
                            </Grid2>
                        </Grid2>
                        {getDialogContent()}
                    </DialogContent>
                    <DialogActions>
                        <Grid2 container width='100%' justifyContent='space-around'>
                            <Button onClick={onPrevStep} color='primary' variant='contained' disabled={step === STEP.INFORMATIONS}>
                                {i18n.previous}
                            </Button>
                            <Button onClick={onNextStep} color='primary' variant='contained' disabled={step === STEP.CONCLUSION}>
                                {i18n.next}
                            </Button>
                        </Grid2>
                    </DialogActions>
                </Dialog>
                <Dialog
                    open={deleteDialogOpen}
                    onClose={() => setDeleteDialogOpen(false)}
                >
                    <DefaultDialogTitle
                        title={i18n.delete}
                        onClose={() => setDeleteDialogOpen(false)}
                    />
                    <DialogContent>
                        <Stack>
                            <h5>{i18n.sureDeleteVisit}</h5>
                            {i18n.chooseDeleteVisit}
                            <Grid2 container justifyContent='end' columnSpacing={2}>
                                <Button
                                    variant='contained'
                                    onClick={() => {
                                        dispatch(CampaignAction.deleteVisit(campaign.id, installationToDelete))
                                            .then(() => dispatch(CampaignAction.fetchCampaignVisits(campaign.id)))
                                        setDeleteDialogOpen(false)
                                    }}
                                >
                                    {i18n.delete}
                                </Button>
                                <Button
                                    variant='contained'
                                    onClick={() => setDeleteDialogOpen(false)}
                                >
                                    {i18n.cancel}
                                </Button>
                            </Grid2>
                        </Stack>
                    </DialogContent>
                </Dialog>
            </Grid2>
        )
    }
    return <ProgressCard style={{ padding: '0.5rem 0.5rem 0.5rem 1rem' }} progress={progress} />
}

InstallationCampaignValidationApp.propTypes = {
    params: PropTypes.shape({
        id: PropTypes.string,
    }),
    campaignVisits: arrayOf(DtoCampaignVisit),
    push: PropTypes.func,
    loadCampaignDashboard: PropTypes.func,
}

export default InstallationCampaignValidationApp
